import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MagnifyingGlass, X, User, EnvelopeSimple, Plus } from '@phosphor-icons/react';
import { userService, type AccountUser } from '../../../../services/widgetService';
import type { Recipient } from '../types';

interface RecipientSelectorProps {
    value: Recipient[];
    onChange: (recipients: Recipient[]) => void;
    disabled?: boolean;
}

type InputMode = 'internal' | 'external';

const RecipientSelector: React.FC<RecipientSelectorProps> = ({
    value,
    onChange,
    disabled = false,
}) => {
    const [inputMode, setInputMode] = useState<InputMode>('internal');
    const [searchTerm, setSearchTerm] = useState('');
    const [externalEmail, setExternalEmail] = useState('');
    const [users, setUsers] = useState<AccountUser[]>([]);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Load users on mount
    useEffect(() => {
        const loadUsers = async () => {
            setLoading(true);
            setError(null);
            try {
                const accountUsers = await userService.getAccountUsers();
                setUsers(accountUsers);
            } catch (err) {
                console.error('Failed to load users:', err);
                setError('Failed to load users');
            } finally {
                setLoading(false);
            }
        };

        loadUsers();
    }, []);

    // Get already selected user IDs for exclusion
    const selectedUserIds = useMemo(() => {
        return new Set(value.filter(r => !r.isExternal && r.userId).map(r => r.userId));
    }, [value]);

    // Get already selected emails for exclusion
    const selectedEmails = useMemo(() => {
        return new Set(value.map(r => r.email.toLowerCase()));
    }, [value]);

    // Filter users based on search term and exclusions
    const filteredUsers = useMemo(() => {
        const term = searchTerm.toLowerCase().trim();

        return users.filter((user) => {
            // Exclude already selected users (by ID or email)
            if (selectedUserIds.has(user.user_uuid)) return false;
            if (selectedEmails.has(user.email.toLowerCase())) return false;

            // Filter by search term
            if (!term) return true;

            const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
            const email = user.email.toLowerCase();

            return fullName.includes(term) || email.includes(term);
        });
    }, [users, searchTerm, selectedUserIds, selectedEmails]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                inputRef.current &&
                !inputRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelectUser = (user: AccountUser) => {
        const newRecipient: Recipient = {
            userId: user.user_uuid,  // Use user_uuid (Snowflake ID) for API
            email: user.email,
            name: `${user.first_name} ${user.last_name}`,
            isExternal: false,
        };
        onChange([...value, newRecipient]);
        setSearchTerm('');
        setIsOpen(false);
    };

    const handleAddExternalEmail = () => {
        const email = externalEmail.trim().toLowerCase();

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Please enter a valid email address');
            return;
        }

        // Check if already added
        if (selectedEmails.has(email)) {
            setError('This email is already added');
            return;
        }

        const newRecipient: Recipient = {
            email,
            isExternal: true,
        };
        onChange([...value, newRecipient]);
        setExternalEmail('');
        setError(null);
    };

    const handleRemoveRecipient = (index: number) => {
        const updated = [...value];
        updated.splice(index, 1);
        onChange(updated);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setIsOpen(true);
        setError(null);
    };

    const handleInputFocus = () => {
        setIsOpen(true);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        inputRef.current?.focus();
    };

    const handleExternalEmailKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddExternalEmail();
        }
    };

    return (
        <div className="space-y-3">
            {/* Mode Toggle */}
            <div className="flex gap-2">
                <button
                    type="button"
                    onClick={() => setInputMode('internal')}
                    disabled={disabled}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                        inputMode === 'internal'
                            ? 'bg-blue-50 border-blue-200 text-blue-700'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    <User size={16} className="inline mr-1.5" weight="bold" />
                    Internal User
                </button>
                <button
                    type="button"
                    onClick={() => setInputMode('external')}
                    disabled={disabled}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                        inputMode === 'external'
                            ? 'bg-blue-50 border-blue-200 text-blue-700'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    <EnvelopeSimple size={16} className="inline mr-1.5" weight="bold" />
                    External Email
                </button>
            </div>

            {/* Input Section */}
            {inputMode === 'internal' ? (
                <div className="relative">
                    <MagnifyingGlass
                        size={18}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                        ref={inputRef}
                        type="text"
                        value={searchTerm}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        placeholder="Search users by name or email..."
                        disabled={disabled || loading}
                        className={`
                            w-full pl-10 pr-10 py-2.5
                            border border-gray-300 rounded-lg
                            text-sm text-gray-900
                            placeholder:text-gray-400
                            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                            disabled:bg-gray-50 disabled:cursor-not-allowed
                        `}
                    />
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={handleClearSearch}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            <X size={16} />
                        </button>
                    )}

                    {/* Dropdown */}
                    {isOpen && !disabled && (
                        <div
                            ref={dropdownRef}
                            className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto"
                        >
                            {loading ? (
                                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                    Loading users...
                                </div>
                            ) : filteredUsers.length === 0 ? (
                                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                    {searchTerm ? 'No users found' : 'Start typing to search'}
                                </div>
                            ) : (
                                <ul className="py-1">
                                    {filteredUsers.slice(0, 8).map((user) => (
                                        <li key={user.id}>
                                            <button
                                                type="button"
                                                onClick={() => handleSelectUser(user)}
                                                className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                                            >
                                                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                                    <User size={16} className="text-blue-600" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 truncate">
                                                        {user.first_name} {user.last_name}
                                                    </p>
                                                    <p className="text-xs text-gray-500 truncate">
                                                        {user.email}
                                                    </p>
                                                </div>
                                            </button>
                                        </li>
                                    ))}
                                    {filteredUsers.length > 8 && (
                                        <li className="px-4 py-2 text-xs text-gray-400 text-center">
                                            +{filteredUsers.length - 8} more. Refine your search.
                                        </li>
                                    )}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex gap-2">
                    <input
                        type="email"
                        value={externalEmail}
                        onChange={(e) => {
                            setExternalEmail(e.target.value);
                            setError(null);
                        }}
                        onKeyDown={handleExternalEmailKeyDown}
                        placeholder="Enter external email address..."
                        disabled={disabled}
                        className={`
                            flex-1 px-3 py-2.5
                            border border-gray-300 rounded-lg
                            text-sm text-gray-900
                            placeholder:text-gray-400
                            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                            disabled:bg-gray-50 disabled:cursor-not-allowed
                            ${error ? 'border-red-300' : ''}
                        `}
                    />
                    <button
                        type="button"
                        onClick={handleAddExternalEmail}
                        disabled={disabled || !externalEmail.trim()}
                        className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <Plus size={18} weight="bold" />
                    </button>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <p className="text-sm text-red-500">{error}</p>
            )}

            {/* Selected Recipients */}
            {value.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {value.map((recipient, index) => (
                        <div
                            key={recipient.isExternal ? recipient.email : recipient.userId}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                                recipient.isExternal
                                    ? 'bg-amber-50 text-amber-800 border border-amber-200'
                                    : 'bg-blue-50 text-blue-800 border border-blue-200'
                            }`}
                        >
                            {recipient.isExternal ? (
                                <EnvelopeSimple size={14} weight="bold" />
                            ) : (
                                <User size={14} weight="bold" />
                            )}
                            <span className="truncate max-w-[150px]">
                                {recipient.name || recipient.email}
                            </span>
                            <button
                                type="button"
                                onClick={() => handleRemoveRecipient(index)}
                                disabled={disabled}
                                className="hover:text-red-600 transition-colors disabled:opacity-50"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {value.length === 0 && (
                <p className="text-sm text-gray-400 italic">
                    No recipients added. The report creator will be automatically included.
                </p>
            )}
        </div>
    );
};

export default RecipientSelector;
